/**
 * R-IMAP-GATEWAY-EDIT (2026-09-19 operatör isteği) — "IMAP Proxy (SOCKS5)"
 * kartı. t-online.de ve tiscali.cz sayfalarında ORTAK bileşen.
 * tiscali.cz POP3 ile okunduğu için orada `protocol="POP3"` verilir ve başlık
 * "POP3 Proxy (SOCKS5)" olur (uç yine /imap-gateway — "mail gateway'i").
 *
 * Operatör, IMAP/POP3'ün geçtiği residential SOCKS5 gateway'ini buradan
 * kaydeder; mail servisi sayfada kayıtlı değeri esas alır (Secret yalnız kayıt
 * yokken varsayılandır) ve en geç 1 dakika içinde yeni proxy ile bağlanır.
 *
 * Sabit IP (sticky, 2026-09-19): gateway her TCP bağlantısında çıkış IP'sini
 * değiştirir. Kullanıcı adında `{session}` varsa sistem onu her kutu için sabit
 * bir oturum kimliğiyle değiştirir; böylece bir kutunun tüm (yeniden) girişleri
 * aynı IP'den çıkar. Kartın yardım satırı bunu anlatır.
 *
 * Gizlilik: şifre ve tam kullanıcı adı sunucudan hiç dönmez; girilen değer
 * password tipinde bir alanda tutulur, başarılı kayıttan sonra silinir ve hiçbir
 * mesajda geri gösterilmez. Durum provider:inspect ile, düzenleme yalnız
 * provider:manage_inventory ile görünür (yetkinin asıl sahibi sunucudur).
 */
import { useRef, useState, type KeyboardEvent } from 'react';

import { useApiMutation, useApiQuery, type QueryState } from '../../api/hooks.js';
import { useCan } from '../../session/can.js';
import { Badge, Button, Card, CardBody, CardHeader, Icon, Input } from '../../ui/index.js';
import {
  fetchImapGateway,
  gatewayReasonText,
  updateImapGateway,
  validateGateway,
  type ImapGatewayProviderId,
  type ImapGatewayStatus,
  type ImapGatewayUpdateInput,
  type ImapGatewayUpdateResponse,
} from './imapGateway.js';

import '../../styles/mailboxunit.mobile.css';

/** Mail protocol carried through the gateway — only changes the operator text. */
export type GatewayProtocol = 'IMAP' | 'POP3';

export interface ImapGatewayCardProps {
  readonly providerId: ImapGatewayProviderId;
  /** Operator-facing provider name, e.g. "t-online.de". */
  readonly providerLabel: string;
  /** Default "IMAP" (t-online.de); tiscali.cz passes "POP3". */
  readonly protocol?: GatewayProtocol;
}

export const IMAP_GATEWAY_SAVED_TEXT =
  'Kaydedildi — tüm sağlayıcıların yeni bağlantıları en geç 1 dakika içinde bu gateway ayarını kullanır.';
export const IMAP_GATEWAY_NOT_CONFIGURED_TEXT =
  'Ortak gateway kayıtlı değil — yeni bağlantılar yapılandırma bekliyor';
/** Sticky-session help line shown on every gateway card. */
export const IMAP_GATEWAY_STICKY_HELP_TEXT =
  'Sabit IP için kullanıcı adına {session} ekleyin (örn. kullanici-session-{session}); sistem her kutu için sabit bir oturum kimliği yerleştirir. Sağlayıcınızın panelindeki sticky biçimini kullanın.';

/** Card title: "IMAP Proxy (SOCKS5)" or "POP3 Proxy (SOCKS5)". */
export function gatewayCardTitle(protocol: GatewayProtocol = 'IMAP'): string {
  return `${protocol} Proxy (SOCKS5)`;
}

function fmt(ts: string | null | undefined): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleString('tr-TR');
}

/**
 * Outer gate: the status needs provider:inspect, so without it nothing renders.
 * Hooks that fetch live in the inner component so the capability check (which
 * flips once the session resolves) never changes the hook order.
 */
export function ImapGatewayCard(props: ImapGatewayCardProps): JSX.Element | null {
  const canInspect = useCan('provider:inspect');
  const canManage = useCan('provider:manage_inventory');
  if (!canInspect) return null;
  return <ImapGatewayCardInner {...props} canManage={canManage} />;
}

function ImapGatewayCardInner({
  providerId,
  providerLabel,
  protocol = 'IMAP',
  canManage,
}: ImapGatewayCardProps & { readonly canManage: boolean }): JSX.Element {
  const status = useApiQuery<ImapGatewayStatus>((o) => fetchImapGateway(o, providerId), [providerId]);
  const mutation = useApiMutation<ImapGatewayUpdateInput, ImapGatewayUpdateResponse>(updateImapGateway);
  // The typed "host:port:user:pass" holds the proxy password, so the input is
  // UNCONTROLLED: React never mirrors it into the `value` attribute (a controlled
  // input would), and a successful save wipes the field directly. Only "is the
  // field non-empty" lives in React state.
  const inputRef = useRef<HTMLInputElement>(null);
  const [hasValue, setHasValue] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const running = mutation.state.kind === 'running';

  function onChange(next: string): void {
    setHasValue(next.length > 0);
    setLocalError(null);
    if (mutation.state.kind === 'ok' || mutation.state.kind === 'error' || mutation.state.kind === 'auth_required') {
      mutation.reset();
    }
  }

  async function save(): Promise<void> {
    const raw = inputRef.current?.value ?? '';
    if (!canManage || running || raw.length === 0) return;
    const checked = validateGateway(raw);
    if (!checked.ok) {
      setLocalError(gatewayReasonText(checked.reason));
      return;
    }
    setLocalError(null);
    const res = await mutation.run({ providerId, gateway: checked.value });
    if (res.kind === 'ok') {
      // Drop the secret from the field at once.
      if (inputRef.current) inputRef.current.value = '';
      setHasValue(false);
      status.reload();
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      void save();
    }
  }

  let serverError: string | null = null;
  if (mutation.state.kind === 'error') {
    serverError =
      mutation.state.reason === 'forbidden'
        ? `Yetki yok: ${mutation.state.missingCapability ?? 'provider:manage_inventory'} izni gerekiyor.`
        : gatewayReasonText(mutation.state.reason);
  } else if (mutation.state.kind === 'auth_required') {
    serverError = gatewayReasonText('auth_required');
  }
  const errorText = localError ?? serverError;
  const inputId = `imap-gateway-input-${providerId}`;
  const helpId = `${inputId}-help`;
  const describedBy = errorText ? `${helpId} ${inputId}-error` : helpId;

  return (
    <Card flush className="imap-gw-card" data-testid="imap-gateway-card" data-provider={providerId} style={{ marginBottom: 16 }}>
      <CardHeader
        title={gatewayCardTitle(protocol)}
        subtitle={
          <>
            Tüm sağlayıcıların kullandığı ortak residential SOCKS5 gateway. Burada yapılan değişiklik
            tüm sağlayıcılara uygulanır; ayar eksikse yeni bağlantılar bekler.
          </>
        }
        actions={
          status.kind === 'ok' ? (
            status.data.configured === true ? (
              <Badge tone="success" dot>ortak gateway</Badge>
            ) : (
              <Badge tone="neutral" dot>yapılandırılmamış</Badge>
            )
          ) : null
        }
      />
      <CardBody>
        <div className="imap-gw-status" data-testid="imap-gateway-status" style={{ marginBottom: canManage ? 12 : 0 }}>
          <GatewayStatusLine status={status} />
        </div>

        {canManage ? (
          <div data-testid="imap-gateway-editor">
            <div className="imap-gw-row" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
              <label htmlFor={inputId} className="t-xs imap-gw-label" style={{ opacity: 0.7 }}>
                Yeni proxy
              </label>
              <Input
                id={inputId}
                data-testid="imap-gateway-input"
                type="password"
                name={`imap-gateway-${providerId}`}
                autoComplete="new-password"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                data-lpignore="true"
                data-1p-ignore="true"
                placeholder="host:port:kullanıcı:şifre"
                ref={inputRef}
                className="imap-gw-input"
                defaultValue=""
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={onKeyDown}
                invalid={localError !== null}
                aria-describedby={describedBy}
                disabled={running}
                style={{ flex: '1 1 240px', minWidth: 0, maxWidth: 420 }}
              />
              <Button
                variant="primary"
                size="sm"
                className="imap-gw-save"
                data-testid="imap-gateway-save"
                disabled={!hasValue}
                loading={running}
                onClick={() => void save()}
                icon={<Icon.Check size={13} />}
              >
                Kaydet
              </Button>
            </div>
            {errorText ? (
              <div
                id={`${inputId}-error`}
                role="alert"
                data-testid="imap-gateway-error"
                className="field-error"
                style={{ marginTop: 6 }}
              >
                {errorText}
              </div>
            ) : null}
            {mutation.state.kind === 'ok' ? (
              <div
                data-testid="imap-gateway-success"
                role="status"
                className="t-small"
                style={{ marginTop: 6, color: 'var(--success-600, #16a34a)', fontWeight: 600 }}
              >
                {IMAP_GATEWAY_SAVED_TEXT}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Sticky-session guidance: shown to every viewer of the card (the
            status line never reveals whether {session} is saved — the server
            only returns the masked user name). */}
        <div
          id={helpId}
          data-testid="imap-gateway-help"
          className="t-small t-tertiary"
          style={{ marginTop: 10 }}
        >
          {IMAP_GATEWAY_STICKY_HELP_TEXT}
        </div>
      </CardBody>
    </Card>
  );
}

function GatewayStatusLine({
  status,
}: {
  readonly status: QueryState<ImapGatewayStatus>;
}): JSX.Element {
  if (status.kind === 'idle' || status.kind === 'loading') {
    return <span className="t-small t-tertiary">Yükleniyor…</span>;
  }
  if (status.kind === 'auth_required') {
    return <span role="alert">{gatewayReasonText('auth_required')}</span>;
  }
  if (status.kind === 'error') {
    const text =
      status.reason === 'forbidden'
        ? `Yetki yok: ${status.missingCapability ?? 'provider:inspect'} izni gerekiyor.`
        : gatewayReasonText(status.reason);
    return (
      <span role="alert">
        <Badge tone="danger" style={{ marginRight: 8 }}>Hata</Badge>
        Proxy durumu okunamadı: {text}
      </span>
    );
  }
  const data = status.data;
  if (data.configured === true) {
    return (
      <span title={data.updatedBy ? `güncelleyen: ${data.updatedBy}` : undefined}>
        Kayıtlı: <span className="t-mono">{data.host}:{data.port}</span> · kullanıcı{' '}
        <span className="t-mono">{data.usernameMasked}</span> · {fmt(data.updatedAt)}
      </span>
    );
  }
  return <span>{IMAP_GATEWAY_NOT_CONFIGURED_TEXT}</span>;
}
